"use client";

import React from "react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import type { EditableCellEditInputsProps } from "../types";
import type { BaseEntity } from "../../../types/index";

/**
 * Inline-edit input for a company's `google_drive_folder_id`.
 *
 * The folder is chosen by browsing Google Drive, not typed — so entering edit
 * mode immediately opens the global ChangeCompanyDriveFolderModal (rooted at the
 * customer-folders root) and exits the inline cell. The modal saves the picked
 * folder via the company update hook. No text is editable here.
 */
export function DriveFolderInput<T extends BaseEntity = BaseEntity>({
  rowId,
  value,
  onCancel,
}: EditableCellEditInputsProps<T>): React.ReactNode {
  const { openModal } = useGlobalModalStack();
  const opened = React.useRef(false);

  React.useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    openModal(MODAL_IDS.companies.changeDriveFolder, {
      companyId: rowId,
      currentFolderId: typeof value === "string" ? value : undefined,
    });
    // Leave inline-edit mode — the modal owns the change from here.
    onCancel();
  }, [openModal, rowId, value, onCancel]);

  return null;
}
